import createEmotion from '@emotion/css/create-instance'
import { CSSPropertiesWithMultiValues } from '@emotion/serialize'
import { CSSProperties } from 'react'

import { ICSSBorderDefinition, CssStyles } from '../types'

const emotion = createEmotion({
  key: 'rbt',
})

export const css = emotion.css

export const getCSSSize = (
  size?: string | number | null,
  fallback = ''
): string => {
  if (size == null || size === '') {
    return fallback
  }

  return typeof size === 'string'
    ? size
    : typeof size === 'number'
    ? `${size}px`
    : fallback
}

export const getCSSBorderDefinition = ({
  style,
  size,
  color,
}: ICSSBorderDefinition): string => {
  return `${style ?? 'none'} ${getCSSSize(size, '0')} ${color ?? 'transparent'}`
}

export const importantify = (style: string | CSSProperties | undefined) => {
  if (!style) return undefined

  return typeof style === 'string'
    ? importantifyString(style)
    : Object.fromEntries(
        Object.entries(style).map(([key, value]) => [
          key,
          typeof value !== 'string' || value.endsWith('!important')
            ? value
            : `${value} !important`,
        ])
      )
}

const importantifyString = (cssString: string) => {
  const lines = cssString
    .split(';')
    .map((line) => line.trim())
    .filter(Boolean)

  const replaced = []
  let inKeyframe = false
  let keyFrameBracketsToClose = 0
  for (const line of lines) {
    if (line.includes('@keyframe')) {
      inKeyframe = true
      keyFrameBracketsToClose += (line.match(/{/g) || []).length
      keyFrameBracketsToClose -= (line.match(/}/g) || []).length
    } else if (inKeyframe) {
      keyFrameBracketsToClose += (line.match(/{/g) || []).length
      keyFrameBracketsToClose -= (line.match(/}/g) || []).length
      if (keyFrameBracketsToClose === 0) {
        inKeyframe = false
        replaced.push(line)
        continue
      }
    }

    if (inKeyframe) {
      replaced.push(line)
    } else {
      replaced.push(line.replace(/(!important)?$/, '!important'))
    }
  }
  return replaced.join(';')
}

export const getClassName = (style: string | CSSProperties | undefined) =>
  css(importantify(style) as string | CSSPropertiesWithMultiValues)

/**
 * @deprecated use `CSSProperties | string` with `getClassName` instead
 */
export const parseClassNames = (
  styles: CssStyles<string>
): CssStyles<string> => {
  const parsedClassNames = Object.entries(styles).map(([key, value]) => {
    return [key, css(importantify(value) as string)]
  })

  return Object.fromEntries(parsedClassNames)
}
